/* AllWizards — consolidated module hosting 23 lean tool wizards.
 * Each wizard is exported individually and registered in ToolWorkspacePage.
 * Simple list-based tools use GenericListWizard; bespoke tools have custom UI.
 */
import { useState } from "react";
import GenericListWizard, { DiagnosticView } from "./GenericListWizard";
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

// ──────────────────────────────────────────────────────
// Generic helpers
const RAG = { R: "#DC2626", A: "#D97706", G: "#16A34A", B: "#3B82F6", X: "#6B7280" };
const ragOptions = [
  { value: "G", label: "🟢 Green" }, { value: "A", label: "🟡 Amber" },
  { value: "R", label: "🔴 Red" }, { value: "B", label: "🔵 Done" },
];
const stageOptions = [
  { value: "submitted", label: "Submitted" }, { value: "triaged", label: "Triaged" },
  { value: "approved", label: "Approved" }, { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" }, { value: "rejected", label: "Rejected" },
];

// ─────────────────────────── C2 — Benefit Case / ROI ───────────────────────────
export const RoiWizard = (props) => <GenericListWizard {...props} schema={{
  key: "roi", completedLabel: "Finish ROI",
  setup: [
    { key: "project_name", label: "Project name" },
    { key: "currency", label: "Currency", type: "text", placeholder: "USD", half: true },
    { key: "discount_rate_pct", label: "Discount rate (%)", type: "number", half: true },
    { key: "horizon_years", label: "Horizon (years)", type: "number", half: true },
    { key: "investment", label: "Initial investment", type: "number", half: true },
    { key: "narrative", label: "Business case narrative", type: "textarea" },
  ],
  captureLabel: "Cash Flows",
  capture: {
    itemKey: "cash_flows", itemLabel: "year-row",
    helpText: "Enter projected savings (positive) and costs (negative) per year. NPV and payback are computed automatically.",
    columns: [
      { key: "year", label: "Year", type: "number", span: 2 },
      { key: "savings", label: "Savings", type: "number", span: 2 },
      { key: "opex_cost", label: "OPEX cost", type: "number", span: 2 },
      { key: "capex_cost", label: "CAPEX cost", type: "number", span: 2 },
      { key: "note", label: "Note", span: 3 },
    ],
    defaultRow: { year: 1, savings: 0, opex_cost: 0, capex_cost: 0, note: "" },
  },
  diagnostic: ({ data }) => {
    const r = (Number(data.discount_rate_pct || 0)) / 100;
    const inv = Number(data.investment || 0);
    const flows = (data.cash_flows || []).map(c => ({
      ...c,
      net: Number(c.savings || 0) - Number(c.opex_cost || 0) - Number(c.capex_cost || 0),
    }));
    let npv = -inv, cum = -inv, payback = null;
    flows.forEach(f => {
      npv += f.net / Math.pow(1 + r, Number(f.year || 1));
      cum += f.net;
      if (payback == null && cum >= 0) payback = f.year;
    });
    const totalSavings = flows.reduce((a, f) => a + Number(f.savings || 0), 0);
    const totalCost = flows.reduce((a, f) => a + Number(f.opex_cost || 0) + Number(f.capex_cost || 0), 0) + inv;
    const roi = totalCost > 0 ? ((totalSavings - totalCost) / totalCost) * 100 : 0;
    return {
      stats: [
        { label: "NPV", value: `${data.currency || "$"}${npv.toFixed(0)}`, accent: npv >= 0 ? "good" : "danger" },
        { label: "ROI", value: `${roi.toFixed(0)}%`, accent: roi >= 0 ? "good" : "danger" },
        { label: "Payback (yr)", value: payback ?? "—" },
        { label: "Net total", value: `${flows.reduce((a, f) => a + f.net, 0).toFixed(0)}` },
      ],
      headers: ["Year", "Savings", "OPEX", "CAPEX", "Net", "Note"],
      rows: flows.map(f => [f.year, f.savings, f.opex_cost, f.capex_cost, f.net.toFixed(0), f.note]),
    };
  },
}} />;

// ─────────────────────────── C3 — Idea Bank ───────────────────────────
export const IdeaBankWizard = (props) => <GenericListWizard {...props} schema={{
  key: "ideabank", completedLabel: "Finish Idea Bank",
  setup: [
    { key: "programme", label: "Programme / portfolio" },
    { key: "owner", label: "Bank owner", half: true }, { key: "review_cadence", label: "Review cadence", half: true, placeholder: "Weekly" },
    { key: "scope", label: "Scope / theme", type: "textarea" },
  ],
  captureLabel: "Ideas",
  capture: {
    itemKey: "ideas", itemLabel: "idea",
    columns: [
      { key: "title", label: "Idea", span: 4 },
      { key: "submitted_by", label: "Submitter", span: 2 },
      { key: "status", label: "Status", type: "select", options: stageOptions, span: 2 },
      { key: "expected_benefit", label: "Benefit ($)", type: "number", span: 2 },
      { key: "owner", label: "Owner", span: 2 },
    ],
    defaultRow: { title: "", submitted_by: "", status: "submitted", expected_benefit: 0, owner: "" },
  },
  diagnostic: ({ data }) => {
    const ideas = data.ideas || [];
    const by = (st) => ideas.filter(i => i.status === st).length;
    const totalBenefit = ideas.filter(i => ["approved", "in_progress", "done"].includes(i.status)).reduce((a, i) => a + Number(i.expected_benefit || 0), 0);
    return {
      stats: [
        { label: "Submitted", value: by("submitted") },
        { label: "Approved", value: by("approved"), accent: "good" },
        { label: "Done", value: by("done"), accent: "good" },
        { label: "Active benefit", value: `$${totalBenefit.toLocaleString()}` },
      ],
      headers: ["Idea", "Submitter", "Status", "Benefit", "Owner"],
      rows: ideas.map(i => [i.title, i.submitted_by, i.status, `$${Number(i.expected_benefit || 0).toLocaleString()}`, i.owner]),
    };
  },
}} />;

// ─────────────────────────── C4 — Standard Work (SWD) ───────────────────────────
export const StandardWorkWizard = (props) => <GenericListWizard {...props} schema={{
  key: "swd", completedLabel: "Finish SOP",
  setup: [
    { key: "process", label: "Process / role" },
    { key: "operator", label: "Operator", half: true }, { key: "takt_min", label: "Takt (min)", type: "number", half: true },
    { key: "tools_needed", label: "Tools / materials needed", type: "textarea" },
    { key: "ppe", label: "PPE / safety", type: "textarea" },
  ],
  captureLabel: "Steps",
  capture: {
    itemKey: "steps", itemLabel: "step",
    helpText: "Order steps top-down. Each step has a cycle time and key point. Total CT vs Takt is shown on Diagnostic.",
    columns: [
      { key: "seq", label: "#", type: "number", span: 1 },
      { key: "task", label: "Task / what", span: 4 },
      { key: "key_point", label: "Key point / how", span: 3 },
      { key: "reason", label: "Why (quality, safety…)", span: 2 },
      { key: "cycle_time_sec", label: "CT (s)", type: "number", span: 2 },
    ],
    defaultRow: { seq: 1, task: "", key_point: "", reason: "", cycle_time_sec: 0 },
  },
  diagnostic: ({ data }) => {
    const steps = data.steps || [];
    const sumS = steps.reduce((a, s) => a + Number(s.cycle_time_sec || 0), 0);
    const taktS = Number(data.takt_min || 0) * 60;
    const balance = taktS > 0 ? (sumS / taktS) * 100 : 0;
    return {
      stats: [
        { label: "Steps", value: steps.length },
        { label: "Total CT", value: `${(sumS / 60).toFixed(2)} min` },
        { label: "Takt", value: `${data.takt_min || 0} min` },
        { label: "Loaded", value: `${balance.toFixed(0)}%`, accent: balance > 100 ? "danger" : balance > 85 ? "warn" : "good" },
      ],
      headers: ["#", "Task", "Key point", "Why", "CT (s)"],
      rows: steps.sort((a, b) => Number(a.seq) - Number(b.seq)).map(s => [s.seq, s.task, s.key_point, s.reason, s.cycle_time_sec]),
    };
  },
}} />;

// ─────────────────────────── C5 — Poka-Yoke Catalog ───────────────────────────
export const PokaYokeWizard = (props) => <GenericListWizard {...props} schema={{
  key: "pokayoke", completedLabel: "Finish catalogue",
  setup: [
    { key: "process", label: "Process / area" },
    { key: "owner", label: "Catalogue owner", half: true }, { key: "review_date", label: "Review date", type: "date", half: true },
    { key: "philosophy", label: "Mistake-proofing philosophy / principles", type: "textarea" },
  ],
  captureLabel: "Devices",
  capture: {
    itemKey: "devices", itemLabel: "device",
    columns: [
      { key: "step", label: "Process step", span: 2 },
      { key: "defect", label: "Defect / error prevented", span: 3 },
      { key: "device", label: "Device / mechanism", span: 3 },
      { key: "type", label: "Type", type: "select", options: ["Prevention", "Detection"], span: 2 },
      { key: "cost", label: "Cost ($)", type: "number", span: 2 },
    ],
    defaultRow: { step: "", defect: "", device: "", type: "Prevention", cost: 0 },
  },
  diagnostic: ({ data }) => {
    const items = data.devices || [];
    const prev = items.filter(i => i.type === "Prevention").length;
    const det = items.filter(i => i.type === "Detection").length;
    const cost = items.reduce((a, i) => a + Number(i.cost || 0), 0);
    return {
      stats: [
        { label: "Devices", value: items.length },
        { label: "Prevention", value: prev, accent: "good" },
        { label: "Detection", value: det },
        { label: "Total cost", value: `$${cost.toLocaleString()}` },
      ],
      headers: ["Step", "Defect", "Device", "Type", "Cost"],
      rows: items.map(i => [i.step, i.defect, i.device, i.type, `$${Number(i.cost || 0).toLocaleString()}`]),
    };
  },
}} />;

// ─────────────────────────── C6 — Kanban / Pull System ───────────────────────────
export const KanbanWizard = (props) => <GenericListWizard {...props} schema={{
  key: "kanban", completedLabel: "Finish Kanban",
  setup: [
    { key: "value_stream", label: "Value stream / line" },
    { key: "demand_per_period", label: "Avg demand / period", type: "number", half: true },
    { key: "lead_time_periods", label: "Replenishment lead time (periods)", type: "number", half: true },
    { key: "safety_factor", label: "Safety factor (e.g. 1.2)", type: "number", half: true },
    { key: "container_qty", label: "Container quantity (qty/card)", type: "number", half: true },
  ],
  captureLabel: "Stages",
  capture: {
    itemKey: "stages", itemLabel: "stage",
    helpText: "Each stage gets a WIP limit. Cards = ⌈(Demand × LT × Safety) / Container qty⌉.",
    columns: [
      { key: "name", label: "Stage", span: 3 },
      { key: "wip_limit", label: "WIP limit", type: "number", span: 2 },
      { key: "demand_share_pct", label: "% of demand", type: "number", span: 2 },
      { key: "policy", label: "Policy / DoR/DoD", span: 4 },
    ],
    defaultRow: { name: "", wip_limit: 1, demand_share_pct: 100, policy: "" },
  },
  diagnostic: ({ data }) => {
    const demand = Number(data.demand_per_period || 0);
    const lt = Number(data.lead_time_periods || 0);
    const sf = Number(data.safety_factor || 1.2);
    const qty = Math.max(1, Number(data.container_qty || 1));
    const stages = data.stages || [];
    const totalWip = stages.reduce((a, s) => a + Number(s.wip_limit || 0), 0);
    const cards = Math.ceil((demand * lt * sf) / qty);
    return {
      stats: [
        { label: "Total cards", value: cards },
        { label: "Stages", value: stages.length },
        { label: "Total WIP", value: totalWip },
        { label: "Container qty", value: qty },
      ],
      headers: ["Stage", "WIP", "% demand", "Policy"],
      rows: stages.map(s => [s.name, s.wip_limit, `${s.demand_share_pct}%`, s.policy]),
    };
  },
}} />;

// ─────────────────────────── C7 — SMED ───────────────────────────
export const SmedWizard = (props) => <GenericListWizard {...props} schema={{
  key: "smed", completedLabel: "Finish SMED",
  setup: [
    { key: "changeover", label: "Changeover / setup" },
    { key: "current_total_min", label: "Current changeover (min)", type: "number", half: true },
    { key: "target_min", label: "Target changeover (min)", type: "number", half: true },
    { key: "frequency_per_week", label: "Frequency / week", type: "number", half: true },
  ],
  captureLabel: "Setup Steps",
  capture: {
    itemKey: "steps", itemLabel: "step",
    helpText: "Classify each step as Internal (only when machine is stopped) or External (can be done while running). SMED converts internal → external first, then reduces both.",
    columns: [
      { key: "task", label: "Task", span: 4 },
      { key: "current_min", label: "Current (min)", type: "number", span: 2 },
      { key: "type", label: "Type", type: "select", options: [{ value: "INT", label: "Internal" }, { value: "EXT", label: "External" }], span: 2 },
      { key: "proposed_type", label: "Proposed", type: "select", options: [{ value: "INT", label: "Internal" }, { value: "EXT", label: "External" }, { value: "ELIM", label: "Eliminate" }], span: 2 },
      { key: "proposed_min", label: "Proposed (min)", type: "number", span: 2 },
    ],
    defaultRow: { task: "", current_min: 0, type: "INT", proposed_type: "INT", proposed_min: 0 },
  },
  diagnostic: ({ data }) => {
    const items = data.steps || [];
    const intCur = items.filter(i => i.type === "INT").reduce((a, i) => a + Number(i.current_min || 0), 0);
    const extCur = items.filter(i => i.type === "EXT").reduce((a, i) => a + Number(i.current_min || 0), 0);
    const intNew = items.filter(i => i.proposed_type === "INT").reduce((a, i) => a + Number(i.proposed_min || 0), 0);
    const extNew = items.filter(i => i.proposed_type === "EXT").reduce((a, i) => a + Number(i.proposed_min || 0), 0);
    const reduction = intCur - intNew;
    const freq = Number(data.frequency_per_week || 0);
    return {
      stats: [
        { label: "Internal now", value: `${intCur} min` },
        { label: "Internal target", value: `${intNew} min`, accent: "good" },
        { label: "Reduction / setup", value: `${reduction} min`, accent: "good" },
        { label: "Hours saved / yr", value: `${((reduction * freq * 52) / 60).toFixed(0)}` },
      ],
      headers: ["Task", "Now", "Type", "→ Proposed", "Proposed (min)"],
      rows: items.map(i => [i.task, i.current_min, i.type, i.proposed_type, i.proposed_min]),
    };
  },
}} />;

// ─────────────────────────── D2 — KPI Scorecard ───────────────────────────
export const KpiScorecardWizard = (props) => <GenericListWizard {...props} schema={{
  key: "kpi", completedLabel: "Finish scorecard",
  setup: [
    { key: "scorecard_name", label: "Scorecard name" },
    { key: "owner", label: "Owner", half: true }, { key: "frequency", label: "Update frequency", half: true, placeholder: "Daily / Weekly / Monthly" },
  ],
  captureLabel: "KPIs",
  capture: {
    itemKey: "kpis", itemLabel: "KPI",
    columns: [
      { key: "tier", label: "Tier", type: "select", options: ["Tier 1", "Tier 2", "Tier 3", "Exec"], span: 2 },
      { key: "name", label: "KPI", span: 3 },
      { key: "target", label: "Target", type: "number", span: 1 },
      { key: "actual", label: "Actual", type: "number", span: 1 },
      { key: "rag", label: "RAG", type: "select", options: ragOptions, span: 1 },
      { key: "owner", label: "Owner", span: 2 },
      { key: "note", label: "Note", span: 2 },
    ],
    defaultRow: { tier: "Tier 1", name: "", target: 0, actual: 0, rag: "G", owner: "", note: "" },
  },
  diagnostic: ({ data }) => {
    const kpis = data.kpis || [];
    const r = kpis.filter(k => k.rag === "R").length, a = kpis.filter(k => k.rag === "A").length, g = kpis.filter(k => k.rag === "G").length;
    return {
      stats: [
        { label: "KPIs", value: kpis.length },
        { label: "Green", value: g, accent: "good" },
        { label: "Amber", value: a, accent: "warn" },
        { label: "Red", value: r, accent: "danger" },
      ],
      headers: ["Tier", "KPI", "Target", "Actual", "RAG", "Owner"],
      rows: kpis.map(k => [k.tier, k.name, k.target, k.actual, k.rag, k.owner]),
    };
  },
}} />;

// ─────────────────────────── D4 — Leader Standard Work (LSW) ───────────────────────────
export const LswWizard = (props) => <GenericListWizard {...props} schema={{
  key: "lsw", completedLabel: "Finish LSW",
  setup: [
    { key: "leader_role", label: "Leader role / title" },
    { key: "leader_name", label: "Leader", half: true }, { key: "shift", label: "Shift / pattern", half: true },
    { key: "purpose", label: "Purpose / value-add focus", type: "textarea" },
  ],
  captureLabel: "Routine",
  capture: {
    itemKey: "items", itemLabel: "routine item",
    helpText: "Capture must-do leader activities. Frequency drives the calendar.",
    columns: [
      { key: "time", label: "Time", span: 2 },
      { key: "activity", label: "Activity", span: 4 },
      { key: "frequency", label: "Frequency", type: "select", options: ["Daily", "Weekly", "Monthly", "Quarterly"], span: 2 },
      { key: "duration_min", label: "Duration (min)", type: "number", span: 2 },
      { key: "category", label: "Category", type: "select", options: ["Gemba", "Tier huddle", "Coaching", "Audit", "Admin"], span: 2 },
    ],
    defaultRow: { time: "", activity: "", frequency: "Daily", duration_min: 15, category: "Gemba" },
  },
  diagnostic: ({ data }) => {
    const items = data.items || [];
    const dailyMin = items.filter(i => i.frequency === "Daily").reduce((a, i) => a + Number(i.duration_min || 0), 0);
    const gembaPct = items.length ? (items.filter(i => i.category === "Gemba").length / items.length) * 100 : 0;
    return {
      stats: [
        { label: "Routine items", value: items.length },
        { label: "Daily minutes", value: dailyMin },
        { label: "Gemba share", value: `${gembaPct.toFixed(0)}%`, accent: gembaPct >= 30 ? "good" : "warn" },
        { label: "Daily hrs", value: `${(dailyMin / 60).toFixed(1)}` },
      ],
      headers: ["Time", "Activity", "Frequency", "Mins", "Category"],
      rows: items.map(i => [i.time, i.activity, i.frequency, i.duration_min, i.category]),
    };
  },
}} />;

// ─────────────────────────── D5 — Layered Process Audit Scheduler ───────────────────────────
export const LpaWizard = (props) => <GenericListWizard {...props} schema={{
  key: "lpa", completedLabel: "Finish LPA",
  setup: [
    { key: "site", label: "Site / area" },
    { key: "programme_owner", label: "Programme owner", half: true }, { key: "audit_year", label: "Audit year", half: true, placeholder: "2026" },
  ],
  captureLabel: "Audits",
  capture: {
    itemKey: "audits", itemLabel: "audit",
    columns: [
      { key: "layer", label: "Layer", type: "select", options: ["Layer 1 — Operator", "Layer 2 — Supervisor", "Layer 3 — Manager", "Layer 4 — Plant"], span: 3 },
      { key: "scope", label: "Scope", span: 3 },
      { key: "auditor", label: "Auditor", span: 2 },
      { key: "scheduled", label: "Scheduled", type: "date", span: 2 },
      { key: "completed", label: "Done", type: "checkbox", span: 1 },
    ],
    defaultRow: { layer: "Layer 1 — Operator", scope: "", auditor: "", scheduled: "", completed: false },
  },
  diagnostic: ({ data }) => {
    const audits = data.audits || [];
    const done = audits.filter(a => a.completed).length;
    const compliance = audits.length ? (done / audits.length) * 100 : 0;
    return {
      stats: [
        { label: "Audits", value: audits.length },
        { label: "Completed", value: done, accent: "good" },
        { label: "Compliance", value: `${compliance.toFixed(0)}%`, accent: compliance >= 90 ? "good" : compliance >= 70 ? "warn" : "danger" },
        { label: "Outstanding", value: audits.length - done },
      ],
      headers: ["Layer", "Scope", "Auditor", "Scheduled", "Done"],
      rows: audits.map(a => [a.layer, a.scope, a.auditor, a.scheduled, a.completed ? "✓" : "—"]),
    };
  },
}} />;

// ─────────────────────────── E2 — TNA ───────────────────────────
export const TnaWizard = (props) => <GenericListWizard {...props} schema={{
  key: "tna", completedLabel: "Finish TNA",
  setup: [
    { key: "team", label: "Team / function" },
    { key: "manager", label: "Manager", half: true }, { key: "review_date", label: "Review date", type: "date", half: true },
  ],
  captureLabel: "Skill Gaps",
  capture: {
    itemKey: "gaps", itemLabel: "skill gap",
    columns: [
      { key: "skill", label: "Skill / capability", span: 3 },
      { key: "demand_fte", label: "Demand (FTE)", type: "number", span: 2 },
      { key: "current_fte", label: "Current (FTE)", type: "number", span: 2 },
      { key: "criticality", label: "Crit (1-5)", type: "number", span: 1 },
      { key: "intervention", label: "Intervention", span: 3 },
    ],
    defaultRow: { skill: "", demand_fte: 0, current_fte: 0, criticality: 3, intervention: "" },
  },
  diagnostic: ({ data }) => {
    const gaps = data.gaps || [];
    const totalGap = gaps.reduce((a, g) => a + Math.max(0, Number(g.demand_fte || 0) - Number(g.current_fte || 0)), 0);
    const critical = gaps.filter(g => Number(g.criticality || 0) >= 4 && Number(g.demand_fte || 0) > Number(g.current_fte || 0)).length;
    return {
      stats: [
        { label: "Skills assessed", value: gaps.length },
        { label: "Net FTE gap", value: totalGap.toFixed(1), accent: totalGap > 0 ? "warn" : "good" },
        { label: "Critical gaps", value: critical, accent: critical ? "danger" : "good" },
        { label: "Surplus skills", value: gaps.filter(g => Number(g.current_fte || 0) > Number(g.demand_fte || 0)).length },
      ],
      headers: ["Skill", "Demand", "Current", "Gap", "Crit", "Intervention"],
      rows: gaps.map(g => [g.skill, g.demand_fte, g.current_fte, Math.max(0, Number(g.demand_fte || 0) - Number(g.current_fte || 0)).toFixed(1), g.criticality, g.intervention]),
    };
  },
}} />;

// ─────────────────────────── E4 — Lean Academy ───────────────────────────
export const AcademyWizard = (props) => <GenericListWizard {...props} schema={{
  key: "academy", completedLabel: "Finish Academy",
  setup: [
    { key: "programme", label: "Programme name" },
    { key: "lead", label: "Programme lead", half: true }, { key: "cohort_year", label: "Cohort year", half: true },
  ],
  captureLabel: "Roster",
  capture: {
    itemKey: "members", itemLabel: "member",
    columns: [
      { key: "name", label: "Name", span: 3 },
      { key: "role", label: "Role", span: 3 },
      { key: "belt", label: "Belt", type: "select", options: ["Yellow", "Green", "Black", "Master Black"], span: 2 },
      { key: "status", label: "Status", type: "select", options: ["Enrolled", "In progress", "Certified", "Lapsed"], span: 2 },
      { key: "cert_date", label: "Cert date", type: "date", span: 1 },
    ],
    defaultRow: { name: "", role: "", belt: "Yellow", status: "Enrolled", cert_date: "" },
  },
  diagnostic: ({ data }) => {
    const m = data.members || [];
    const certs = m.filter(x => x.status === "Certified");
    return {
      stats: [
        { label: "Members", value: m.length },
        { label: "Certified", value: certs.length, accent: "good" },
        { label: "Black Belts", value: certs.filter(x => x.belt === "Black" || x.belt === "Master Black").length },
        { label: "In progress", value: m.filter(x => x.status === "In progress").length },
      ],
      headers: ["Name", "Role", "Belt", "Status", "Cert date"],
      rows: m.map(x => [x.name, x.role, x.belt, x.status, x.cert_date]),
    };
  },
}} />;

// ─────────────────────────── D3 — Tier Meeting Designer ───────────────────────────
export const TierMeetingWizard = (props) => <GenericListWizard {...props} schema={{
  key: "tiermtg", completedLabel: "Finish design",
  setup: [
    { key: "site", label: "Site / value stream" },
    { key: "tier_lead", label: "Programme lead", half: true }, { key: "cadence", label: "Cadence", half: true },
  ],
  captureLabel: "Huddle Agenda",
  capture: {
    itemKey: "agenda", itemLabel: "agenda item",
    helpText: "Design the agenda items per tier. Tier 1 = team / floor, Tier 2 = supervisor, Tier 3 = leadership.",
    columns: [
      { key: "tier", label: "Tier", type: "select", options: ["Tier 1", "Tier 2", "Tier 3"], span: 2 },
      { key: "item", label: "Agenda item", span: 4 },
      { key: "duration_min", label: "Mins", type: "number", span: 1 },
      { key: "owner", label: "Owner", span: 2 },
      { key: "trigger_up", label: "Escalation trigger", span: 3 },
    ],
    defaultRow: { tier: "Tier 1", item: "", duration_min: 5, owner: "", trigger_up: "" },
  },
  diagnostic: ({ data }) => {
    const a = data.agenda || [];
    const tierMin = ["Tier 1", "Tier 2", "Tier 3"].map(t => ({ t, m: a.filter(i => i.tier === t).reduce((s, i) => s + Number(i.duration_min || 0), 0) }));
    return {
      stats: tierMin.map(t => ({ label: t.t, value: `${t.m} min`, accent: t.m > 30 ? "warn" : "good" })),
      headers: ["Tier", "Agenda item", "Mins", "Owner", "Escalates to…"],
      rows: a.map(i => [i.tier, i.item, i.duration_min, i.owner, i.trigger_up]),
    };
  },
}} />;

// ─────────────────────────── F3 — Attrition Cohort Modeller ───────────────────────────
export const AttritionWizard = (props) => <GenericListWizard {...props} schema={{
  key: "attrition", completedLabel: "Finish model",
  setup: [
    { key: "function_name", label: "Function / role" },
    { key: "starting_headcount", label: "Starting headcount", type: "number", half: true },
    { key: "annual_attrition_pct", label: "Annual attrition %", type: "number", half: true },
    { key: "monthly_hires", label: "Monthly hires (steady)", type: "number", half: true },
    { key: "horizon_months", label: "Horizon (months)", type: "number", half: true },
  ],
  captureLabel: "Cohort Adjustments",
  capture: {
    itemKey: "cohorts", itemLabel: "cohort event",
    helpText: "Optional: layer in special hiring waves or attrition shocks at specific months.",
    columns: [
      { key: "month", label: "Month", type: "number", span: 2 },
      { key: "delta_hires", label: "+ Hires", type: "number", span: 2 },
      { key: "delta_attrition_pct", label: "Δ attrition %", type: "number", span: 2 },
      { key: "note", label: "Note", span: 6 },
    ],
    defaultRow: { month: 1, delta_hires: 0, delta_attrition_pct: 0, note: "" },
  },
  diagnostic: ({ data }) => {
    const start = Number(data.starting_headcount || 0);
    const annA = Number(data.annual_attrition_pct || 0) / 100;
    const mHires = Number(data.monthly_hires || 0);
    const horizon = Number(data.horizon_months || 12);
    const cohortByMonth = (data.cohorts || []).reduce((acc, c) => { acc[Number(c.month || 0)] = c; return acc; }, {});
    let hc = start; const trend = [];
    for (let m = 1; m <= horizon; m++) {
      const c = cohortByMonth[m] || { delta_hires: 0, delta_attrition_pct: 0 };
      const monthlyAttrition = (annA + Number(c.delta_attrition_pct || 0) / 100) / 12;
      hc = hc * (1 - monthlyAttrition) + mHires + Number(c.delta_hires || 0);
      trend.push({ month: m, headcount: hc });
    }
    return {
      stats: [
        { label: "Start HC", value: start },
        { label: "End HC", value: trend[trend.length - 1]?.headcount.toFixed(0) || "—" },
        { label: "Min HC", value: Math.min(...trend.map(t => t.headcount)).toFixed(0) },
        { label: "Steady-state hires/yr", value: mHires * 12 },
      ],
      headers: ["Month", "Headcount"],
      rows: trend.map(t => [t.month, t.headcount.toFixed(1)]),
    };
  },
}} />;

// ─────────────────────────── F4 — Automation Opportunity Scorer ───────────────────────────
export const AutomationScorerWizard = (props) => <GenericListWizard {...props} schema={{
  key: "automation", completedLabel: "Finish scoring",
  setup: [
    { key: "portfolio", label: "Portfolio / function" },
    { key: "owner", label: "Scoring owner", half: true }, { key: "review_date", label: "Review date", type: "date", half: true },
  ],
  captureLabel: "Candidates",
  capture: {
    itemKey: "candidates", itemLabel: "candidate",
    helpText: "Score 1–5 across volume, rule-based, structured-data, error-rate, value. Composite ranks candidates.",
    columns: [
      { key: "name", label: "Process / task", span: 3 },
      { key: "volume", label: "Volume", type: "number", span: 1 },
      { key: "rules", label: "Rules", type: "number", span: 1 },
      { key: "structured", label: "Struct", type: "number", span: 1 },
      { key: "error_rate", label: "Errors", type: "number", span: 1 },
      { key: "value", label: "Value", type: "number", span: 1 },
      { key: "tech", label: "Tech", type: "select", options: ["RPA", "AI/ML", "BPM", "Hybrid"], span: 2 },
      { key: "owner", label: "Owner", span: 2 },
    ],
    defaultRow: { name: "", volume: 3, rules: 3, structured: 3, error_rate: 3, value: 3, tech: "RPA", owner: "" },
  },
  diagnostic: ({ data }) => {
    const items = (data.candidates || []).map(c => ({ ...c, _score: ["volume", "rules", "structured", "error_rate", "value"].reduce((a, k) => a + Number(c[k] || 0), 0) }));
    items.sort((a, b) => b._score - a._score);
    const high = items.filter(c => c._score >= 20).length;
    return {
      stats: [
        { label: "Candidates", value: items.length },
        { label: "Top score", value: items[0]?._score || 0 },
        { label: "Quick wins (≥20)", value: high, accent: "good" },
        { label: "Avg score", value: items.length ? (items.reduce((a, c) => a + c._score, 0) / items.length).toFixed(1) : 0 },
      ],
      headers: ["Process", "Tech", "V", "R", "S", "E", "Val", "Score"],
      rows: items.map(c => [c.name, c.tech, c.volume, c.rules, c.structured, c.error_rate, c.value, c._score]),
    };
  },
}} />;

// ─────────────────────────── DILO — Day In Life Of ───────────────────────────
export const DiloWizard = (props) => <GenericListWizard {...props} schema={{
  key: "dilo", completedLabel: "Finish DILO",
  setup: [
    { key: "role", label: "Role observed" },
    { key: "observer", label: "Observer", half: true }, { key: "observation_date", label: "Date", type: "date", half: true },
    { key: "shift_minutes", label: "Shift duration (min)", type: "number", half: true },
  ],
  captureLabel: "Activity Log",
  capture: {
    itemKey: "activities", itemLabel: "activity",
    helpText: "Sequential log of activities through the day, classified by VA / NVA / Wait. Diagnostic computes time split.",
    columns: [
      { key: "start", label: "Start", span: 1 },
      { key: "end", label: "End", span: 1 },
      { key: "activity", label: "Activity", span: 4 },
      { key: "duration_min", label: "Min", type: "number", span: 1 },
      { key: "type", label: "Type", type: "select", options: [
        { value: "VA", label: "Value-Add" }, { value: "NVA", label: "Non-VA" },
        { value: "BNVA", label: "Business-NVA" }, { value: "WAIT", label: "Wait" },
      ], span: 2 },
      { key: "system", label: "System / tool", span: 2 },
    ],
    defaultRow: { start: "", end: "", activity: "", duration_min: 0, type: "VA", system: "" },
  },
  diagnostic: ({ data }) => {
    const a = data.activities || [];
    const tot = a.reduce((s, x) => s + Number(x.duration_min || 0), 0);
    const va = a.filter(x => x.type === "VA").reduce((s, x) => s + Number(x.duration_min || 0), 0);
    const nva = a.filter(x => x.type === "NVA").reduce((s, x) => s + Number(x.duration_min || 0), 0);
    const wait = a.filter(x => x.type === "WAIT").reduce((s, x) => s + Number(x.duration_min || 0), 0);
    return {
      stats: [
        { label: "Activities", value: a.length },
        { label: "VA share", value: tot ? `${((va / tot) * 100).toFixed(0)}%` : "—", accent: va / tot >= 0.3 ? "good" : "warn" },
        { label: "NVA min", value: nva, accent: "danger" },
        { label: "Wait min", value: wait, accent: "warn" },
      ],
      headers: ["Start", "End", "Activity", "Min", "Type", "System"],
      rows: a.map(x => [x.start, x.end, x.activity, x.duration_min, x.type, x.system]),
    };
  },
}} />;
